import { AsYouTypeFormatter, PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';

const phoneUtil = PhoneNumberUtil.getInstance();

/**
 * Validates a phone number using Google's libphonenumber library.
 * @param phone - The phone number to validate (should include country code, e.g. "+14155552671")
 * @returns true if the phone number is a valid number for its region, false otherwise
 */
export const isPhoneValid = (phone: string): boolean => {
    if (!phone || phone.trim() === '') {
        return false;
    }

    try {
        const parsedNumber = phoneUtil.parseAndKeepRawInput(phone);
        return phoneUtil.isValidNumber(parsedNumber);
    } catch {
        return false;
    }
};

/**
 * Digits in the national (subscriber) part of a number — i.e. excluding the
 * country calling code. `+1 415 555 2671` → 10.
 *
 * Mechanics only, no policy: callers decide what length they accept (the lead
 * form caps answers at `MAX_PHONE_NATIONAL_DIGITS`). Returns 0 for anything
 * unparseable, which every caller already treats as "not a usable number".
 */
export const nationalDigitCount = (phone: string): number => {
    if (!phone?.trim()) return 0;

    try {
        const parsed = phoneUtil.parseAndKeepRawInput(phone);
        return phoneUtil.getNationalSignificantNumber(parsed).length;
    } catch {
        return 0;
    }
};

/**
 * Extracts the country ISO2 code from a phone number.
 * @param phoneNumber - The phone number to extract the country from (should include country code)
 * @param defaultCountry - ISO2 code returned when extraction fails (default: 'us')
 * @returns lowercase ISO2 country code (e.g. 'us', 'ca', 'gb')
 */
export const getCountryFromPhone = (phoneNumber: string, defaultCountry = 'us'): string => {
    if (!phoneNumber?.trim()) {
        return defaultCountry;
    }

    try {
        const parsedNumber = phoneUtil.parseAndKeepRawInput(phoneNumber);
        const regionCode = phoneUtil.getRegionCodeForNumber(parsedNumber);
        return regionCode ? regionCode.toLowerCase() : defaultCountry;
    } catch {
        return defaultCountry;
    }
};

/**
 * Groups an E.164 number for *display* as it is typed — `+14155552671`
 * renders as `+1 415-555-2671`. Formatting is presentational only; keep the
 * raw E.164 in state, since that is what the API expects.
 *
 * Falls back to the input unchanged for partial or unrecognised numbers, so a
 * half-typed value never disappears mid-entry.
 */
export const formatPhoneAsYouType = (phone: string, region = 'US'): string => {
    if (!phone) return '';

    try {
        const formatter = new AsYouTypeFormatter(region);
        let formatted = phone;
        // AsYouTypeFormatter is stateful: feeding it one character at a time is
        // the documented usage, and the last return value is the full result.
        for (const char of phone) {
            formatted = formatter.inputDigit(char);
        }
        return formatted || phone;
    } catch {
        return phone;
    }
};

export interface IPhoneDescription {
    /** Lowercase ISO2 for a flag, or null when the number cannot be parsed. */
    readonly iso2: string | null;
    /** The international display form (`+1 415-555-2671`), or the input unchanged. */
    readonly formatted: string;
}

/**
 * What a read-only number shows: a flag and the international grouping.
 * A number whose area code the metadata does not know (the 555 test range)
 * still gets its calling code's main country — `+1` → US — so the flag is
 * never blank for a real-looking number. Unparseable input is shown as typed.
 */
export const describePhone = (phone: string): IPhoneDescription => {
    const raw = phone.trim();
    if (!raw) return { iso2: null, formatted: '' };
    try {
        const parsed = phoneUtil.parse(raw);
        const region =
            phoneUtil.getRegionCodeForNumber(parsed) ??
            phoneUtil.getRegionCodeForCountryCode(parsed.getCountryCode() ?? 0);
        const iso2 = region && region !== 'ZZ' ? region.toLowerCase() : null;
        return { iso2, formatted: phoneUtil.format(parsed, PhoneNumberFormat.INTERNATIONAL) };
    } catch {
        return { iso2: null, formatted: raw };
    }
};
